import React, { useRef, useState } from 'react';
import { Button, Modal } from 'antd';
import { CheckOutlined, UploadOutlined } from '@ant-design/icons';
import { uploadApi } from '../services/api-services';
import { resolveUrl } from '../utils/url';

export const PRESET_IMAGES = [
  // 头像
  '/avatars/bottts-robot1.svg', '/avatars/bottts-robot2.svg', '/avatars/bottts-robot3.svg',
  '/avatars/bottts-robot4.svg', '/avatars/bottts-robot5.svg', '/avatars/bottts-agent1.svg',
  '/avatars/bottts-nova.svg', '/avatars/bottts-orion.svg', '/avatars/bottts-pixel.svg', '/avatars/bottts-spark.svg',
  '/avatars/adventurer-alice.svg', '/avatars/adventurer-bob.svg', '/avatars/adventurer-charlie.svg',
  '/avatars/adventurer-diana.svg', '/avatars/adventurer-evan.svg', '/avatars/adventurer-fiona.svg',
  '/avatars/adventurer-grace.svg', '/avatars/adventurer-henry.svg',
  '/avatars/lorelei-luna.svg', '/avatars/lorelei-max.svg', '/avatars/lorelei-nina.svg',
  '/avatars/lorelei-oscar.svg', '/avatars/lorelei-petra.svg', '/avatars/lorelei-quinn.svg',
  '/avatars/micah-alex.svg', '/avatars/micah-blake.svg', '/avatars/micah-casey.svg',
  '/avatars/micah-drew.svg', '/avatars/micah-eden.svg', '/avatars/micah-frankie.svg',
  '/avatars/pixel-art-hero1.svg', '/avatars/pixel-art-hero2.svg', '/avatars/pixel-art-hero3.svg',
  '/avatars/pixel-art-hero4.svg', '/avatars/pixel-art-hero5.svg', '/avatars/pixel-art-hero6.svg',
  '/avatars/thumbs-sunny.svg', '/avatars/thumbs-cloudy.svg', '/avatars/thumbs-breezy.svg',
  '/avatars/thumbs-stormy.svg', '/avatars/thumbs-misty.svg', '/avatars/thumbs-windy.svg',
  // Emoji
  '🤖', '🧠', '💬', '🗣️', '👾', '🦾',
  '📊', '📈', '📉', '🔢', '🧮', '📡',
  '📝', '📋', '📄', '🗒️', '📑', '🗃️',
  '🔍', '🛠️', '⚙️', '🔧', '🔩', '🧰',
  '🎨', '✏️', '🖌️', '🎭', '🖼️', '💎',
  '🏢', '💼', '📦', '🛒', '💰', '📣',
  '💻', '🖥️', '📱', '⌨️', '🖱️', '🔌',
  '🌐', '🔐', '🛡️', '🔑', '☁️', '🌍',
  '🔔', '🚀', '🎯', '⚡', '🔄', '📌',
  '👥', '🤝', '🏆', '🎓', '💡', '🧩',
  // 应用图标
  '/app-icons/openai.svg', '/app-icons/anthropic.svg', '/app-icons/google.svg',
  '/app-icons/microsoftazure.svg', '/app-icons/amazonaws.svg', '/app-icons/googlecloud.svg',
  '/app-icons/github.svg', '/app-icons/gitlab.svg', '/app-icons/docker.svg',
  '/app-icons/kubernetes.svg', '/app-icons/terraform.svg', '/app-icons/jenkins.svg',
  '/app-icons/python.svg', '/app-icons/javascript.svg', '/app-icons/typescript.svg',
  '/app-icons/react.svg', '/app-icons/vuedotjs.svg', '/app-icons/nodedotjs.svg',
  '/app-icons/mysql.svg', '/app-icons/postgresql.svg', '/app-icons/mongodb.svg',
  '/app-icons/redis.svg', '/app-icons/elasticsearch.svg', '/app-icons/sqlite.svg',
  '/app-icons/slack.svg', '/app-icons/discord.svg', '/app-icons/notion.svg',
  '/app-icons/wechat.svg', '/app-icons/telegram.svg', '/app-icons/whatsapp.svg',
  '/app-icons/linkedin.svg', '/app-icons/twitter.svg', '/app-icons/youtube.svg',
  '/app-icons/bilibili.svg', '/app-icons/zhihu.svg',
  '/app-icons/shopify.svg', '/app-icons/stripe.svg', '/app-icons/paypal.svg',
  '/app-icons/salesforce.svg', '/app-icons/hubspot.svg', '/app-icons/zendesk.svg',
  '/app-icons/grafana.svg', '/app-icons/prometheus.svg',
  '/app-icons/canva.svg', '/app-icons/sketch.svg', '/app-icons/adobephotoshop.svg',
  '/app-icons/wordpress.svg', '/app-icons/apple.svg', '/app-icons/meta.svg',
];

interface ImagePickerProps {
  value?: string;
  onChange?: (v: string) => void;
  /** 预览区域的形状，默认 circle */
  shape?: 'circle' | 'square';
  size?: number;
}

const ImagePicker: React.FC<ImagePickerProps> = ({ value, onChange, shape = 'circle', size = 64 }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res: any = await uploadApi.image(file);
      onChange?.(res.url);
    } catch {
      const reader = new FileReader();
      reader.onload = () => onChange?.(reader.result as string);
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const isImage = value && (
    value.startsWith('http') || value.startsWith('data:') ||
    value.startsWith('/uploads/') || value.startsWith('/avatars/') || value.startsWith('/app-icons/')
  );
  const borderRadius = shape === 'circle' ? '50%' : 12;

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div
          onClick={() => fileInputRef.current?.click()}
          style={{
            width: size, height: size, borderRadius, cursor: 'pointer', flexShrink: 0,
            border: '2px dashed var(--ini-border, #d9d9d9)', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', fontSize: 10, color: 'var(--muted-text, #8c8c8c)',
            overflow: 'hidden', background: 'var(--apple-bg-secondary, #f5f5f5)',
          }}
        >
          {value
            ? (isImage
                ? <img src={isImage ? resolveUrl(value) : value} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <span style={{ fontSize: size * 0.45 }}>{value}</span>)
            : <><UploadOutlined style={{ fontSize: 18, marginBottom: 2 }} /><span>上传</span></>}
        </div>
        <Button size="small" onClick={() => setPickerOpen(true)}>选择</Button>
        <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} />
      </div>

      <Modal title="选择图标 / 头像" open={pickerOpen} onCancel={() => setPickerOpen(false)} footer={null} width={520}>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6,
          padding: '4px 2px', maxHeight: '50vh', overflowY: 'auto', overflowX: 'hidden',
        }}>
          {PRESET_IMAGES.map((img) => {
            const selected = value === img;
            const isImg = img.startsWith('/');
            return (
              <div
                key={img}
                onClick={() => { onChange?.(img); setPickerOpen(false); }}
                style={{
                  width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer', borderRadius: 8, fontSize: 22, position: 'relative',
                  border: selected ? '2px solid #818cf8' : '1px solid transparent',
                  background: selected ? 'rgba(129,140,248,0.1)' : undefined,
                  transition: 'all 0.15s',
                }}
              >
                {isImg
                  ? <img src={img} alt="" style={{ width: 30, height: 30, objectFit: 'contain', borderRadius: shape === 'circle' ? '50%' : 4 }} />
                  : img}
                {selected && (
                  <div style={{
                    position: 'absolute', bottom: -3, right: -3,
                    background: '#818cf8', borderRadius: '50%',
                    width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CheckOutlined style={{ color: '#fff', fontSize: 9 }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--ini-border, #f0f0f0)' }}>
          <Button icon={<UploadOutlined />} block onClick={() => { setPickerOpen(false); fileInputRef.current?.click(); }}>
            上传本地图片
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default ImagePicker;
